/**
 * Our Broking — Accessibility Widget v5.4
 * WCAG 2.1 AA · GIGW · Section 508
 *
 * v5.4:
 *  - Invert Colours reverted to original CSS filter approach
 *    (single invert(1) on .app-shell, image counter-invert).
 *  - High Contrast tuned: visibly distinct from default
 *    (bold text, strong borders on interactive elements,
 *    flat — no shadows/gradients) but no forced borders on
 *    every card/section, no hover-invert overacting.
 *
 * Features:
 *   1. Text Size       — WCAG 1.4.4
 *   2. Invert Colours  — CSS filter, no layout impact
 *   3. High Contrast   — WCAG 1.4.6 AAA
 *   4. Highlight Links — GIGW
 *   5. Pause Animations— WCAG 2.3.3
 *   6. Big Cursor      — WCAG 2.5.5
 */

import React, {
  useState, useEffect, useRef, useCallback, useReducer
} from 'react';
import ReactDOM from 'react-dom';
import './AccessibilityWidget.css';

/* ── Constants ───────────────────────────────────────────── */
const STORAGE_KEY = 'ob_a11y_v5';
const MIN_FONT    = 80;
const MAX_FONT    = 200;
const FONT_STEP   = 10;

const DEFAULTS = {
  fontSize:        100,
  invertColors:    false,
  highContrast:    false,
  highlightLinks:  false,
  pauseAnimations: false,
  cursor:          'default',
};

/* ── Persistence ─────────────────────────────────────────── */
function loadSettings() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return { ...DEFAULTS, ...JSON.parse(raw) };
  } catch (_) {}
  return { ...DEFAULTS };
}

function saveSettings(s) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch (_) {}
}

/* ── Reducer ─────────────────────────────────────────────── */
function reducer(state, action) {
  switch (action.type) {
    case 'SET':   return { ...state, [action.key]: action.val };
    case 'RESET': return { ...DEFAULTS };
    default:      return state;
  }
}

/* ── Apply all settings to DOM ───────────────────────────── */
function applyToDom(s) {
  const html     = document.documentElement;
  const appShell = document.querySelector('.app-shell');

  /* 1 — Font size */
  html.style.fontSize = s.fontSize + '%';

  /* 2 — Invert colours: CSS filter on .app-shell only.
        Widget is portalled to <body>, so it's untouched.   */
  if (appShell) {
    appShell.style.filter = s.invertColors ? 'invert(1)' : '';
  }

  /* 3-5 — Class-based toggles on <html> */
  html.classList.toggle('ob-high-contrast',    s.highContrast);
  html.classList.toggle('ob-highlight-links',  s.highlightLinks);
  html.classList.toggle('ob-pause-animations', s.pauseAnimations);

  /* 6 — Cursor */
  html.classList.remove('ob-cursor-white', 'ob-cursor-black');
  if (s.cursor !== 'default') {
    html.classList.add(`ob-cursor-${s.cursor}`);
  }

  saveSettings(s);
}

/* ── Toggle component ────────────────────────────────────── */
function Toggle({ id, label, checked, onChange }) {
  return (
    <label className="ob-toggle-row" htmlFor={id}>
      <span className="ob-toggle-label">{label}</span>
      <span className="ob-toggle">
        <input
          type="checkbox"
          id={id}
          role="switch"
          checked={checked}
          aria-checked={checked}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span className="ob-toggle-track" aria-hidden="true">
          <span className="ob-toggle-thumb" />
        </span>
      </span>
    </label>
  );
}

/* ── Cursor picker ───────────────────────────────────────── */
function CursorPicker({ value, onChange }) {
  const options = [
    { val: 'default', label: 'Default' },
    { val: 'white',   label: 'Large White' },
    { val: 'black',   label: 'Large Black' },
  ];
  return (
    <div className="ob-cursor-group" role="group" aria-label="Cursor size and colour">
      {options.map(o => (
        <button
          key={o.val}
          className={`ob-cursor-btn ob-cursor-btn--${o.val}${value === o.val ? ' ob-cursor-btn--active' : ''}`}
          aria-pressed={value === o.val}
          onClick={() => onChange(o.val)}
        >
          <span className="ob-cursor-preview" aria-hidden="true" />
          <span>{o.label}</span>
        </button>
      ))}
    </div>
  );
}

/* ── Main widget (inner) ─────────────────────────────────── */
function AccessibilityWidgetInner() {
  const [isOpen, setIsOpen] = useState(false);
  const [s, dispatch]       = useReducer(reducer, undefined, loadSettings);
  const triggerRef          = useRef(null);
  const closeRef            = useRef(null);
  const panelRef            = useRef(null);

  useEffect(() => { applyToDom(s); }, [s]);

  useEffect(() => {
    const saved = loadSettings();
    Object.entries(saved).forEach(([key, val]) =>
      dispatch({ type: 'SET', key, val })
    );
  }, []); // eslint-disable-line

  useEffect(() => {
    const fn = (e) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', fn);
    return () => document.removeEventListener('keydown', fn);
  }, [isOpen]);

  useEffect(() => {
    const fn = (e) => {
      if (!isOpen) return;
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        panelRef.current   && !panelRef.current.contains(e.target)
      ) setIsOpen(false);
    };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) setTimeout(() => closeRef.current?.focus(), 60);
  }, [isOpen]);

  const set   = useCallback((key, val) => dispatch({ type: 'SET', key, val }), []);
  const reset = useCallback(() => {
    dispatch({ type: 'RESET' });
    applyToDom(DEFAULTS);
  }, []);

  return (
    <>
      <button
        ref={triggerRef}
        id="ob-a11y-trigger"
        className={isOpen ? 'ob-trigger-open' : ''}
        aria-label="Open accessibility options"
        aria-expanded={isOpen}
        aria-controls="ob-a11y-panel"
        aria-haspopup="dialog"
        onClick={() => setIsOpen(o => !o)}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"
          aria-hidden="true" focusable="false">
          <circle cx="12" cy="4" r="2"/>
          <path d="M12 7c-2.67 0-8 1.34-8 4v1h16v-1c0-2.66-5.33-4-8-4z"/>
          <path d="M8.5 13l-1.5 8h2l1-5 2 2v5h2v-6l-2-2 .5-3"/>
          <path d="M15.5 13l1.5 8h-2l-1-5"/>
        </svg>
        <span className="ob-trigger-label">Accessibility</span>
      </button>

      <div
        ref={panelRef}
        id="ob-a11y-panel"
        role="dialog"
        aria-label="Accessibility settings"
        aria-modal="true"
        aria-hidden={!isOpen}
        className={isOpen ? 'ob-panel-open' : ''}
        tabIndex={-1}
        {...(!isOpen ? { inert: '' } : {})}
      >
        <div className="ob-panel-header">
          <span className="ob-panel-title">Accessibility Options</span>
          <button
            ref={closeRef}
            className="ob-close-btn"
            aria-label="Close accessibility panel"
            onClick={() => setIsOpen(false)}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
              aria-hidden="true">
              <line x1="18" y1="6"  x2="6"  y2="18"/>
              <line x1="6"  y1="6"  x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div className="ob-panel-body">

          <section className="ob-group" aria-labelledby="ob-font-label">
            <span className="ob-group-label" id="ob-font-label">Text Size</span>
            <div className="ob-stepper" role="group" aria-label="Text size controls">
              <button
                className="ob-step-btn"
                disabled={s.fontSize <= MIN_FONT}
                aria-label={`Decrease text size, currently ${s.fontSize}%`}
                onClick={() => set('fontSize', Math.max(MIN_FONT, s.fontSize - FONT_STEP))}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="3" strokeLinecap="round" aria-hidden="true">
                  <line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
              </button>
              <output className="ob-step-value" aria-live="polite" aria-atomic="true">
                {s.fontSize}%
              </output>
              <button
                className="ob-step-btn"
                disabled={s.fontSize >= MAX_FONT}
                aria-label={`Increase text size, currently ${s.fontSize}%`}
                onClick={() => set('fontSize', Math.min(MAX_FONT, s.fontSize + FONT_STEP))}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="3" strokeLinecap="round" aria-hidden="true">
                  <line x1="12" y1="5" x2="12" y2="19"/>
                  <line x1="5"  y1="12" x2="19" y2="12"/>
                </svg>
              </button>
            </div>
          </section>

          <section className="ob-group" aria-labelledby="ob-cursor-label">
            <span className="ob-group-label" id="ob-cursor-label">Cursor</span>
            <CursorPicker value={s.cursor} onChange={v => set('cursor', v)} />
          </section>

          <section className="ob-group" aria-labelledby="ob-display-label">
            <span className="ob-group-label" id="ob-display-label">Display</span>
            <Toggle
              id="ob-invert"
              label="Invert Colours"
              checked={s.invertColors}
              onChange={v => set('invertColors', v)}
            />
            <Toggle
              id="ob-high-contrast"
              label="High Contrast"
              checked={s.highContrast}
              onChange={v => set('highContrast', v)}
            />
            <Toggle
              id="ob-highlight"
              label="Highlight Links"
              checked={s.highlightLinks}
              onChange={v => set('highlightLinks', v)}
            />
            <Toggle
              id="ob-pause-anim"
              label="Pause Animations"
              checked={s.pauseAnimations}
              onChange={v => set('pauseAnimations', v)}
            />
          </section>

        </div>

        <div className="ob-panel-footer">
          <button className="ob-reset-btn" onClick={reset}
            aria-label="Reset all accessibility settings to default">
            Reset to Default
          </button>
        </div>
      </div>
    </>
  );
}

/* ── Portal wrapper ──────────────────────────────────────── */
function AccessibilityWidget() {
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);
  if (!mounted) return null;
  return ReactDOM.createPortal(<AccessibilityWidgetInner />, document.body);
}

export default AccessibilityWidget;